import { Button, Chip, ChipProps, IconButton, Link as MuiLink, Menu, MenuItem, TableCell, TableRow, Tooltip } from '@mui/material';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { BrickwiseTable } from 'src/components/mui-overrides/BrickwiseTable';
import { useTransactions } from 'src/hooks/useTransactions';
import { TransactionFilterDialog } from 'src/components/dialogs/TransactionFilterDialog';
import { TransactionResponse } from '../models/transaction';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { RefundDialog } from '../components/dialogs/RefundDialog';
import { DetailDrawer } from 'src/components/DetailDrawer';
import { DetailView, formatDetailDate } from 'src/components/DetailView';
import { ConfirmationDialog } from 'src/components/ui/ConfirmationDialog';
import { CopyToClipboardButton } from 'src/components/ui/CopyToClipboardButton';
import { postReversePrimaryForUser } from 'src/api/oApi';
import { generalStore } from 'src/stores/GeneralStore';
import { routes } from 'src/router/AppRouter';

export const TransactionSite: FC<{ userIdValue?: string }> = ({ userIdValue }: { userIdValue?: string }) => {
  const { t } = useTranslation();
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(0);
  const [name, setName] = useState('');
  const [status, setStatus] = useState(undefined);
  const [method, setMethod] = useState(undefined);
  const [type, setType] = useState(undefined);
  const [userId, setId] = useState(userIdValue);
  const setUserId = userIdValue
    ? () => {
        console.log('');
      }
    : setId;
  const [from, setFrom] = useState(undefined);
  const [to, setTo] = useState(undefined);
  const [sort, setSort] = useState<'oldest' | 'newest'>('newest');
  const [detail, setDetail] = useState<TransactionResponse | null>(null);
  const transactions = useTransactions(
    {
      page,
      to,
      from,
      method,
      status,
      type,
      sort,
      userId,
    },
    reload,
  );

  return (
    <>
      <TransactionFilterDialog
        userId={userId}
        setUserId={setUserId}
        type={type}
        setType={setType}
        method={method}
        setMethod={setMethod}
        firstName={name}
        setFirstName={setName}
        from={from}
        setFrom={setFrom}
        to={to}
        setTo={setTo}
        status={status}
        setStatus={setStatus}
        setPage={setPage}
        sort={sort}
        setSort={setSort}
      />

      <div style={{ margin: '16px' }}>
        {[
          { field: status, name: 'Status', setFunction: setStatus },
          { field: method, name: 'Method', setFunction: setMethod },
          { field: type, name: 'Type', setFunction: setType },
          { field: to, name: 'To', setFunction: setTo },
          { field: from, name: 'From', setFunction: setFrom },
          { field: userId, name: 'User', setFunction: setUserId },
        ].map(({ field, name, setFunction }) => {
          if (field) {
            return (
              <Chip
                id={'chip-' + name}
                label={name + ':' + field}
                onDelete={() => {
                  setFunction(undefined);
                }}
              />
            );
          } else {
            return null;
          }
        })}
      </div>
      <BrickwiseTable
        page={page}
        rowsPerPage={10}
        hidePagination={false}
        hideChangePageLengthChange={false}
        length={transactions?.count ?? 0}
        setPage={setPage}
        renderHeader={() => {
          return (
            <TableRow>
              <TableCell>{t('users.table.name')}</TableCell>
              <TableCell style={{ textAlign: 'center' }}>{t('transactions.table.type')}</TableCell>
              <TableCell style={{ textAlign: 'center' }}>{t('transactions.table.date')}</TableCell>
              <TableCell style={{ textAlign: 'center' }}>{t('transactions.table.status')}</TableCell>
              <TableCell style={{ textAlign: 'center' }}>{t('transactions.table.method')}</TableCell>
              <TableCell style={{ textAlign: 'center' }}>{t('transactions.table.app')}</TableCell>
              <TableCell style={{ textAlign: 'center' }}>{t('transactions.table.amount')}</TableCell>
              <TableCell style={{ textAlign: 'center' }}>{t('transactions.table.title')}</TableCell>
            </TableRow>
          );
        }}
        renderRows={() => {
          return (
            <>
              {transactions?.data.map((row, index) => {
                const name = row.user?.firstName ? row.user?.firstName + ' ' + row.user?.lastName : '';
                return (
                  <TableRow
                    onClick={() => {
                      setDetail(row);
                    }}
                    key={index}
                    hover
                    style={{ cursor: 'pointer', textDecoration: 'none' }}
                  >
                    <TableCell>{name}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>{row.transaction.type}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>{row.transaction.createdAt}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>{row.transaction.status}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>{row.transaction.method}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>{row.transaction.transactionInformation?.senderApp}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>{row.transaction.amount as unknown as string}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TransactionMenu row={row} reload={() => setReload(!reload)} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </>
          );
        }}
      />
      <DetailDrawer open={!!detail} onClose={() => setDetail(null)} title="Transaction">
        {detail ? (
          <TransactionDetail
            row={detail}
            onReversed={() => {
              setDetail(null);
              setReload((current) => !current);
            }}
          />
        ) : null}
      </DetailDrawer>
    </>
  );
};

const transactionStatusChipColor = (status?: string): ChipProps['color'] => {
  switch (status?.toLowerCase()) {
    case 'success':
      return 'success';
    case 'error':
    case 'canceled':
      return 'error';
    case 'reserved':
    case 'pending':
    case 'charged':
      return 'warning';
    default:
      return 'default';
  }
};

const TransactionDetail: FC<{ row: TransactionResponse; onReversed: () => void }> = ({ row, onReversed }) => {
  const { transaction, user } = row;
  const [reverseConfirmationOpen, setReverseConfirmationOpen] = useState(false);
  const name = user?.firstName ? `${user.firstName} ${user.lastName}` : undefined;
  const isPrimaryBuy =
    transaction.type === 'buy' && transaction.tradeInformation?.tradingPlace === 'OTC' && transaction.status === 'reserved';

  const reversePrimary = async () => {
    try {
      generalStore.setIsLoading(true);
      await postReversePrimaryForUser({ userId: transaction.sender, assetId: transaction.assetId });
      generalStore.setMessage('Primary transaction reversed successfully');
      setReverseConfirmationOpen(false);
      onReversed();
    } catch (error: any) {
      generalStore.setError(
        error?.response?.data?.error?.message || error?.response?.data?.error || error?.message || 'Error on Operation',
      );
    } finally {
      generalStore.setIsLoading(false);
    }
  };

  return (
    <>
      <ConfirmationDialog
        title="Reverse this primary transaction?"
        submitBtnLabel="Reverse"
        isOpened={reverseConfirmationOpen}
        close={() => setReverseConfirmationOpen(false)}
        onSubmit={reversePrimary}
      />
      <DetailView
        chips={[
          { label: transaction.type, color: 'default' },
          { label: transaction.status, color: transactionStatusChipColor(transaction.status) },
          { label: transaction.method, color: 'default' },
        ]}
        headline={transaction.amount !== undefined ? `${transaction.amount} €` : undefined}
        subheadline={[transaction.title, formatDetailDate(transaction.createdAt)].filter(Boolean).join(' — ')}
        actions={
          isPrimaryBuy ? (
            <Button variant="contained" color="error" onClick={() => setReverseConfirmationOpen(true)}>
              Reverse primary transaction
            </Button>
          ) : null
        }
        sections={[
          {
            title: 'Overview',
            fields: [
              {
                label: 'Transaction ID',
                value: transaction.id ? <CopyToClipboardButton text={transaction.id} label={transaction.id} /> : undefined,
              },
              { label: 'Title', value: transaction.title },
              { label: 'Amount', value: transaction.amount },
              { label: 'Shares', value: transaction.shares },
              { label: 'Share price', value: transaction.sharePrice },
              {
                label: 'Fee',
                value: transaction.fee?.amount !== undefined ? `${transaction.fee.amount} (${transaction.fee.percentage}%)` : undefined,
              },
              { label: 'Created at', value: formatDetailDate(transaction.createdAt) },
              { label: 'Updated at', value: formatDetailDate(transaction.updatedAt) },
            ],
          },
          {
            title: 'Parties',
            fields: [
              {
                label: 'User',
                value: user?.userId ? (
                  <MuiLink component={RouterLink} to={routes.USERDETAIL.replace(':id', user.userId)}>
                    {name || user.userId}
                  </MuiLink>
                ) : (
                  name
                ),
              },
              {
                label: 'Sender',
                value: transaction.sender ? <CopyToClipboardButton text={transaction.sender} label={transaction.sender} /> : undefined,
              },
              {
                label: 'Receiver',
                value: transaction.receiver ? (
                  <CopyToClipboardButton text={transaction.receiver} label={transaction.receiver} />
                ) : undefined,
              },
            ],
          },
          {
            title: 'Trade',
            fields: [
              { label: 'Trading place', value: transaction.tradeInformation?.tradingPlace },
              { label: 'Trading type', value: transaction.tradeInformation?.tradingType },
              { label: 'ISIN', value: transaction.tradeInformation?.ISIN },
              {
                label: 'Asset ID',
                value: transaction.assetId ? <CopyToClipboardButton text={transaction.assetId} label={transaction.assetId} /> : undefined,
              },
              { label: 'Offer ID', value: transaction.offerId },
            ],
          },
          {
            title: 'Payment',
            fields: [
              { label: 'Method', value: transaction.method },
              { label: 'Provider transaction ID', value: transaction.paymentProviderInformation?.transactionId },
              { label: 'Sender app', value: transaction.transactionInformation?.senderApp },
            ],
          },
        ]}
        raw={row}
      />
    </>
  );
};

interface MenuProps {
  row: TransactionResponse;
  reload: () => void;
}

export const TransactionMenu: FC<MenuProps> = ({ row, reload }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const { transaction } = row;

  const open = !!anchorEl;
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setRefundDialogOpen(false);
  };
  const isRefundEnabled = transaction.status.toLowerCase() === 'success' && transaction.type === 'charge';

  return (
    <>
      <RefundDialog open={refundDialogOpen} transaction={transaction} handleClose={handleClose} triggerRefresh={reload} />
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          handleClick(event);
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Tooltip title="Only possible for successful charges" open={isRefundEnabled ? false : undefined}>
          {/* Span, as otherwise the tooltip won't be shown if MenuItem is disabled */}
          <span>
            <MenuItem onClick={() => setRefundDialogOpen(true)} disabled={!isRefundEnabled}>
              {t('transactions.table.action.refund')}
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
};
